import { useStoryBibleStore } from "src/stores/storyBibleStore";

type CharacterUpdateChunk = {
  characterId?: string;
  projectId?: string;
  character?: CharacterEntity;
  oldFields?: Record<string, unknown>;
  newFields?: Record<string, unknown>;
};

type MetaUpdateChunk = {
  updatedFields?: string[];
  oldFields?: Record<string, unknown>;
  newFields?: Record<string, unknown>;
};

const META_STORY_BIBLE_SECTION_IDS = new Set(["synopsis", "seed", "genre", "style"]);

const META_SECTION_TO_UPDATE_FIELD: Record<string, string> = {
  synopsis: "synopsis",
  seed: "braindump",
  genre: "genre",
  style: "style",
};

export function isMetaStoryBibleSection(sectionId: string): boolean {
  return META_STORY_BIBLE_SECTION_IDS.has(sectionId);
}

/** Merge all streaming meta_update newFields in order (later chunks win per key). */
export function collectMergedMetaNewFieldsFromUpdates(
  metaUpdates: MetaUpdateChunk[],
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const update of metaUpdates) {
    const newFields = update.newFields && typeof update.newFields === "object" ? update.newFields : {};
    for (const [key, value] of Object.entries(newFields)) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
  }
  return merged;
}

/** Collect updated field keys from every meta_update chunk in the stream. */
export function collectMetaUpdatedFieldKeys(metaUpdates: MetaUpdateChunk[]): string[] {
  const keys = new Set<string>();
  for (const update of metaUpdates) {
    for (const fieldKey of update.updatedFields ?? []) {
      if (typeof fieldKey === "string" && fieldKey) {
        keys.add(fieldKey);
      }
    }
  }
  return [...keys];
}

/** Re-apply all known meta_update payloads after a story-engine GET. */
export function reapplyMetaDiffsFromUpdates(
  store: StoryBibleEntityStore,
  metaUpdates: MetaUpdateChunk[],
  projectId: string,
): void {
  const newFields = collectMergedMetaNewFieldsFromUpdates(metaUpdates);
  if (Object.keys(newFields).length === 0) return;
  reapplyMetaUpdateFromChunk(store, { newFields }, projectId);
}

/** Merge streaming meta_update newFields for a Story Bible section card (synopsis, seed, etc.). */
export function collectMetaNewFieldsForStoryBibleSection(
  sectionId: string,
  metaUpdates: MetaUpdateChunk[],
): Record<string, unknown> {
  const fieldKey = META_SECTION_TO_UPDATE_FIELD[sectionId];
  if (!fieldKey) return {};

  const merged: Record<string, unknown> = {};
  for (const update of metaUpdates) {
    const newFields = update.newFields && typeof update.newFields === "object" ? update.newFields : {};
    if (newFields[fieldKey] !== undefined) {
      merged[fieldKey] = newFields[fieldKey];
    }
  }
  return merged;
}

/** Validate both project and load lifetime against the live shared projection. */
export function isCurrentStoryBibleOwner(store: Pick<StoryBibleEntityStore, "owner">, projectId: string): boolean {
  return Boolean(store.owner && store.owner.projectId === projectId && useStoryBibleStore.getState().owner === store.owner);
}

export function applyStoryEngineMetaToStore(
  store: StoryBibleEntityStore,
  projectId: string,
  results: unknown,
): boolean {
  if (!isCurrentStoryBibleOwner(store, projectId)) return false;
  const storyEngine = (results as { storyEngine?: { seed?: unknown; synopsis?: unknown; metadata?: unknown } } | null)
    ?.storyEngine;
  if (!storyEngine || typeof storyEngine !== "object") {
    return false;
  }

  store.setSeed(typeof storyEngine.seed === "string" ? storyEngine.seed : "");
  store.setSynopsis(typeof storyEngine.synopsis === "string" ? storyEngine.synopsis : "", projectId);
  store.setMetadata(() => (storyEngine.metadata && typeof storyEngine.metadata === "object" ? storyEngine.metadata : {}));
  return true;
}

async function awaitStoryBibleMetaRefresh(store: StoryBibleEntityStore, projectId: string): Promise<void> {
  if (!isCurrentStoryBibleOwner(store, projectId)) return;
  const owner = store.owner;
  const generation = startMetaUpdateRefetch();
  const Auth = (await import("src/Auth")).default;
  const results = await Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {});
  if (!isLatestMetaUpdateRefetch(generation)) {
    return;
  }
  if (useStoryBibleStore.getState().owner !== owner) return;
  applyStoryEngineMetaToStore(store, projectId, results);
}

type StoryBibleEntityStore = {
  owner?: { projectId: string } | null;
  charactersEntities?: Array<{ id?: string; [key: string]: unknown }> | null;
  worldbuildingEntries?: Array<{ id?: string; [key: string]: unknown }> | null;
  characterDiffs?: Record<string, { oldFields?: Record<string, unknown>; newFields?: Record<string, unknown> }>;
  worldbuildingDiffs?: Record<string, { oldFields?: Record<string, unknown>; newFields?: Record<string, unknown> }>;
  pendingNewCharacterIds?: string[];
  pendingNewWorldbuildingIds?: string[];
  upsertCharacterEntity: (character: { id: string; [key: string]: unknown }, projectId?: string) => void;
  upsertWorldbuildingEntity: (entry: { id: string; [key: string]: unknown }) => void;
  setSeed: (seed: string) => void;
  setSynopsis: (synopsis: string, projectId?: string) => void;
  setMetadata: (fn: (draft: Record<string, unknown>) => void) => void;
  refreshCharacters: (projectId: string, options?: { force?: boolean }) => Promise<void>;
  refreshWorldbuildingEntries: (
    projectId: string,
    source?: string,
    options?: { force?: boolean },
  ) => Promise<void>;
};

let characterUpdateRefetchGeneration = 0;
let worldbuildingUpdateRefetchGeneration = 0;
let metaUpdateRefetchGeneration = 0;

/** Mark the start of an async character refetch; returns a generation token for ordering. */
export function startCharacterUpdateRefetch(): number {
  characterUpdateRefetchGeneration += 1;
  return characterUpdateRefetchGeneration;
}

export function isLatestCharacterUpdateRefetch(generation: number): boolean {
  return generation === characterUpdateRefetchGeneration;
}

export function startWorldbuildingUpdateRefetch(): number {
  worldbuildingUpdateRefetchGeneration += 1;
  return worldbuildingUpdateRefetchGeneration;
}

export function isLatestWorldbuildingUpdateRefetch(generation: number): boolean {
  return generation === worldbuildingUpdateRefetchGeneration;
}

export function startMetaUpdateRefetch(): number {
  metaUpdateRefetchGeneration += 1;
  return metaUpdateRefetchGeneration;
}

export function isLatestMetaUpdateRefetch(generation: number): boolean {
  return generation === metaUpdateRefetchGeneration;
}

/** @internal Test helper */
export function resetStoryBibleUpdateRefetchGenerationsForTests(): void {
  characterUpdateRefetchGeneration = 0;
  worldbuildingUpdateRefetchGeneration = 0;
  metaUpdateRefetchGeneration = 0;
}

function asPlainString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

type CharacterEntity = {
  id: string;
  name?: unknown;
  roles?: unknown;
  pronouns?: unknown;
  groups?: unknown;
  otherNames?: unknown;
  customFields?: Array<{ id?: string; label?: string; value?: unknown }>;
  displayOrder?: number;
  [key: string]: unknown;
};

/** Only complete structured payloads may enter editable entity state.
 * Markdown newFields are display summaries; list refresh supplies saved rich text.
 */
export function applyCharacterUpdateFromChunk(store: StoryBibleEntityStore, chunk: CharacterUpdateChunk): void {
  if (chunk.characterId && chunk.character?.id === chunk.characterId) {
    store.upsertCharacterEntity(chunk.character, chunk.projectId);
  }
}

/**
 * Apply meta_update SSE payload synchronously so Story Bible fields show new
 * content before async story-engine GET completes (which may return stale data).
 */
export function applyMetaUpdateFromChunk(
  store: StoryBibleEntityStore,
  chunk: MetaUpdateChunk,
  projectId: string,
): void {
  if (!isCurrentStoryBibleOwner(store, projectId)) return;
  const newFields = chunk.newFields && typeof chunk.newFields === "object" ? chunk.newFields : {};
  if (Object.keys(newFields).length === 0) return;

  if (newFields.braindump !== undefined) {
    store.setSeed(asPlainString(newFields.braindump));
  }
  if (newFields.synopsis !== undefined) {
    store.setSynopsis(asPlainString(newFields.synopsis), projectId);
  }
  if (
    newFields.genre !== undefined
    || newFields.style !== undefined
    || (newFields.metadata !== undefined && typeof newFields.metadata === "object")
  ) {
    store.setMetadata((draft) => {
      if (newFields.genre !== undefined) draft.genre = asPlainString(newFields.genre);
      if (newFields.style !== undefined) draft.style = asPlainString(newFields.style);
      if (newFields.metadata !== undefined && typeof newFields.metadata === "object") {
        Object.assign(draft, newFields.metadata as Record<string, unknown>);
      }
    });
  }
}

export function reapplyMetaUpdateFromChunk(
  store: StoryBibleEntityStore,
  chunk: MetaUpdateChunk,
  projectId: string,
): void {
  if (!isCurrentStoryBibleOwner(store, projectId)) return;
  applyMetaUpdateFromChunk(store, chunk, projectId);
}

/** Wait for Story Bible entity refresh before clearing diff overlays after generation. */
export async function awaitStoryBibleRefreshForGeneratedEntity(
  store: StoryBibleEntityStore,
  entity: { entityType: string; entityId: string },
  projectId: string,
): Promise<void> {
  if (entity.entityType === "character") {
    await store.refreshCharacters(projectId, { force: true });
    return;
  }
  if (entity.entityType === "worldbuilding") {
    await store.refreshWorldbuildingEntries(projectId, "storyBibleUpdateFromChunk.awaitGeneratedEntity", { force: true });
    return;
  }
  if (entity.entityType === "story_bible_section" && entity.entityId === "characters") {
    await store.refreshCharacters(projectId, { force: true });
    return;
  }
  if (entity.entityType === "story_bible_section" && entity.entityId === "worldbuilding") {
    await store.refreshWorldbuildingEntries(projectId, "storyBibleUpdateFromChunk.awaitGeneratedEntity", { force: true });
    return;
  }
  if (entity.entityType === "story_bible_section" && isMetaStoryBibleSection(entity.entityId)) {
    await awaitStoryBibleMetaRefresh(store, projectId);
  }
}

export type {
  CharacterUpdateChunk,
  MetaUpdateChunk,
  StoryBibleEntityStore,
};
